import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { ThemeToggle } from '@/components/ThemeToggle';
import logo from '@/assets/logo.png';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Privacy Policy - How We Protect Your Data | Sentinel"
        description="Read Sentinel's comprehensive privacy policy. Learn how we collect, use, and protect your financial data with industry-leading security."
      />
      
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-background/95 backdrop-blur-xl sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                <img src={logo} alt="SENTINEL Logo" className="w-10 h-10 brightness-0 dark:brightness-100 dark:invert" />
                <span className="text-2xl font-bold">SENTINEL</span>
              </div>
              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-20 max-w-4xl">
          <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-muted-foreground mb-12">Last updated: January 2025</p>

          <Card className="p-8 mb-8 border-border/50">
            <div className="prose prose-slate dark:prose-invert max-w-none space-y-6">
              <section>
                <h2 className="text-3xl font-bold mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Sentinel, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our financial reconciliation platform. By using Sentinel, you agree to the collection and use of information in accordance with this policy.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">2. Information We Collect</h2>
                <h3 className="text-xl font-semibold mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Account information (name, email, phone number)</li>
                  <li>Financial data (bank transactions, invoices, Tally entries)</li>
                  <li>Business information (company name, GST number)</li>
                  <li>Payment information (processed securely through payment providers)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Provide Services:</strong> Process reconciliations, detect fraud, generate reports</li>
                  <li><strong>Improve Platform:</strong> Analyze usage patterns to enhance features and user experience</li>
                  <li><strong>Communicate:</strong> Send service updates, security alerts, and support responses</li>
                  <li><strong>Security:</strong> Protect against unauthorized access, fraud, and security threats</li>
                  <li><strong>Compliance:</strong> Meet legal obligations and regulatory requirements</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">4. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We implement industry-leading security measures to protect your data:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Encryption:</strong> AES-256 encryption for data at rest and TLS 1.3 for data in transit</li>
                  <li><strong>Access Controls:</strong> Row-level security policies and role-based access control</li>
                  <li><strong>Infrastructure:</strong> Hosted on secure, compliant cloud infrastructure</li>
                  <li><strong>Monitoring:</strong> 24/7 security monitoring and regular vulnerability assessments</li>
                  <li><strong>Backups:</strong> Regular encrypted backups with disaster recovery procedures</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">5. Data Sharing and Disclosure</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We do not sell your personal information. We may share data only in these circumstances:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Service Providers:</strong> Third-party vendors who assist in providing our services (e.g., cloud hosting, payment processing)</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">6. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We retain your data for as long as your account is active or as needed to provide services. Financial transaction data is retained according to legal and regulatory requirements (typically 7 years). You can request deletion of your data at any time, subject to legal obligations.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">7. Your Rights</h2>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                  <li><strong>Export:</strong> Download your data in a portable format</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">8. Cookies and Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookies through your browser settings, though some features may not function properly if you disable cookies.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">9. International Data Transfers</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your data is primarily stored in secure data centers in India. If data is transferred internationally, we ensure appropriate safeguards are in place to protect your information in accordance with applicable laws.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">10. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Sentinel is not intended for children under 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">11. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy periodically. We will notify you of material changes via email or prominent notice on our platform. Your continued use after changes indicates acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">12. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions about this Privacy Policy or to exercise your rights, contact us at:
                </p>
                <div className="mt-4 p-4 bg-surface rounded-lg">
                  <p className="font-medium mb-2">Email: privacy@sentinel.com</p>
                  <p className="text-muted-foreground">Response time: Within 48 hours</p>
                </div>
              </section>
            </div>
          </Card>
        </div>

        <footer className="border-t border-border/50 py-8 bg-surface/30">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>&copy; 2025 SENTINEL. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
